import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import { suggestObligationMappings, applyObligationMappings } from 'services/ProgrammeService'
import { getProgramme } from 'services/checklistService'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import PerUnitEvidenceModal from 'features/compliance/PerUnitEvidenceModal'

const TIER = {
  ready: { label: 'Ready', text: '#15803d', bg: '#dcfce7' },
  reclassify: { label: 'Will reclassify', text: '#d97706', bg: '#fef3c7' },
  needs_tags: { label: 'Needs tags', text: '#475569', bg: '#f1f5f9' },
  needs_date: { label: 'Needs a date', text: '#d97706', bg: '#fef3c7' },
}

const UNMET = ['pending', 'expired', 'in_review']

const MapExistingFilesModal = ({ bpId, onClose, onApplied }) => {
  const [suggestions, setSuggestions] = useState([])
  const [unitObligations, setUnitObligations] = useState([])
  const [bpName, setBpName] = useState(null)
  const [loading, setLoading] = useState(true)
  const [chosen, setChosen] = useState({})
  const [saving, setSaving] = useState(false)
  const [insightFile, setInsightFile] = useState(null)
  const [perUnitTarget, setPerUnitTarget] = useState(null)

  const reload = () => {
    setLoading(true)
    Promise.all([suggestObligationMappings(bpId), getProgramme(bpId)])
      .then(([mapRes, progRes]) => {
        const list = mapRes.data?.data || []
        setSuggestions(list)
        setChosen(Object.fromEntries(list.map(s => [s.obligationId, true])))

        const prog = progRes.data?.data || null
        setBpName(prog?.buildingPassport?.name || null)
        const allObligations = (prog?.domains || []).flatMap(d => d.obligations || [])
        setUnitObligations(
          allObligations.filter(o => o.scope === 'unit' && !o.isSkipped && UNMET.includes(o.status))
        )
      })
      .catch(() => toast.error("Couldn't work out which documents match."))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([suggestObligationMappings(bpId), getProgramme(bpId)])
      .then(([mapRes, progRes]) => {
        if (!alive) return
        const list = mapRes.data?.data || []
        setSuggestions(list)
        setChosen(Object.fromEntries(list.map(s => [s.obligationId, true])))
        const prog = progRes.data?.data || null
        setBpName(prog?.buildingPassport?.name || null)
        const allObligations = (prog?.domains || []).flatMap(d => d.obligations || [])
        setUnitObligations(
          allObligations.filter(o => o.scope === 'unit' && !o.isSkipped && UNMET.includes(o.status))
        )
      })
      .catch(() => { if (alive) toast.error("Couldn't work out which documents match.") })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [bpId])

  const selectedCount = useMemo(() => Object.values(chosen).filter(Boolean).length, [chosen])

  const openInsight = s => {
    if (!s?.fileId) return
    setInsightFile({ id: s.fileId, displayName: s.fileDisplayName, buildingPassportId: bpId, buildingPassportName: bpName })
  }

  const apply = () => {
    if (saving || selectedCount === 0) return
    const mappings = suggestions
      .filter(s => chosen[s.obligationId])
      .map(s => ({ obligationId: s.obligationId, fileId: s.fileId }))
    setSaving(true)
    applyObligationMappings(bpId, mappings)
      .then(res => {
        const satisfied = res?.data?.data?.satisfied ?? 0
        toast.success(`Mapped ${mappings.length} document${mappings.length === 1 ? '' : 's'}: ${satisfied} obligation${satisfied === 1 ? '' : 's'} now satisfied.`)
        onApplied?.()
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not apply those mappings.'))
      .finally(() => setSaving(false))
  }

  const hasMatches = suggestions.length > 0
  const hasUnits = unitObligations.length > 0

  return createPortal(
    <>
      <Backdrop onClick={onClose}>
        <Panel onClick={e => e.stopPropagation()}>
          <Header>
            <Title>Match your existing documents</Title>
            <Sub>
              We scanned this building's documents for ones that satisfy unmet obligations. Open a document's
              insight to check it, then apply the matches you agree with. Nothing changes until you do.
            </Sub>
          </Header>

          <Body>
            {loading ? (
              [0, 1, 2, 3].map(i => <Skeleton key={i} />)
            ) : (!hasMatches && !hasUnits) ? (
              <Empty>
                <strong>No matches to review.</strong>
                <div>None of this building's documents clearly match an unmet obligation. You can still link documents one by one from each obligation.</div>
              </Empty>
            ) : (
              <>
                {hasMatches && (
                  <Section>
                    <SectionHead>Matched documents · {suggestions.length}</SectionHead>
                    {suggestions.map(s => {
                      const tier = TIER[s.tier] || TIER.reclassify
                      const on = !!chosen[s.obligationId]
                      return (
                        <Row key={s.obligationId} $active={on} onClick={() => setChosen(p => ({ ...p, [s.obligationId]: !on }))}>
                          <Check $on={on}>{on && '✓'}</Check>
                          <RowText>
                            <Pair>
                              <Obligation>{s.obligationName}</Obligation>
                              <ArrowForwardRoundedIcon style={{ fontSize: 14, color: '#94a3b8', flexShrink: 0 }} />
                              <FileBtn type='button' onClick={e => { e.stopPropagation(); openInsight(s) }} title='Open Guru insight for this document'>
                                <DescriptionRoundedIcon style={{ fontSize: 14, flexShrink: 0 }} />
                                <FileName>{s.fileDisplayName}</FileName>
                              </FileBtn>
                            </Pair>
                            <Meta>
                              <Badge $text={tier.text} $bg={tier.bg}>{tier.label}</Badge>
                              {!s.inDate && <Badge $text='#b00020' $bg='#fdecea'>Expired</Badge>}
                              {s.fileTypeName && <TypeChip>{s.fileTypeName}</TypeChip>}
                              <Reason>{s.reason}</Reason>
                            </Meta>
                          </RowText>
                          <InsightBtn type='button' onClick={e => { e.stopPropagation(); openInsight(s) }} title='Open Guru insight'>
                            <AutoAwesomeRoundedIcon style={{ fontSize: 15 }} /> Insight
                          </InsightBtn>
                        </Row>
                      )
                    })}
                  </Section>
                )}

                {hasUnits && (
                  <Section>
                    <SectionHead>Per-unit obligations · {unitObligations.length}</SectionHead>
                    <SectionNote>These need a document on <strong>each unit</strong>, so they can't be matched from the building's files. Review each one to see which units are still missing evidence.</SectionNote>
                    {unitObligations.map(o => {
                      const cov = o.unitCoverage || {}
                      const covered = cov.covered ?? cov.satisfied ?? null
                      const applicable = cov.applicable ?? cov.total ?? null
                      return (
                        <UnitRow key={o.id}>
                          <UnitIcon><LayersRoundedIcon style={{ fontSize: 17 }} /></UnitIcon>
                          <RowText>
                            <Obligation>{o.name}</Obligation>
                            <Meta>
                              <Badge $text='#0e7490' $bg='#e0f2fe'>Per-unit</Badge>
                              {(covered !== null && applicable !== null) && (
                                <Reason>{covered} of {applicable} unit{applicable === 1 ? '' : 's'} covered</Reason>
                              )}
                              {o.fileType?.name && <TypeChip>{o.fileType.name}</TypeChip>}
                            </Meta>
                          </RowText>
                          <ReviewBtn type='button' onClick={() => setPerUnitTarget(o)}>Review per unit</ReviewBtn>
                        </UnitRow>
                      )
                    })}
                  </Section>
                )}
              </>
            )}
          </Body>

          <Footer>
            <Cancel onClick={onClose}>Close</Cancel>
            {hasMatches && (
              <Save disabled={saving || selectedCount === 0} onClick={apply}>
                {saving ? 'Applying…' : `Apply ${selectedCount} match${selectedCount === 1 ? '' : 'es'}`}
              </Save>
            )}
          </Footer>
        </Panel>
      </Backdrop>

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          onUpdate={reload}
          file={insightFile}
          bpId={bpId}
          buildingPassportName={bpName}
        />
      )}
      {perUnitTarget && (
        <PerUnitEvidenceModal
          bpId={bpId}
          obligation={perUnitTarget}
          onClose={() => setPerUnitTarget(null)}
          onChanged={reload}
        />
      )}
    </>,
    document.body
  )
}

export default MapExistingFilesModal

const pulse = keyframes`0%{opacity:.55}50%{opacity:1}100%{opacity:.55}`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1300; padding: 20px;
`

const Panel = styled.div`
  background: #fff; border-radius: 14px;
  width: 100%; max-width: 680px; max-height: calc(100vh - 48px);
  display: flex; flex-direction: column;
  box-shadow: 0 20px 48px rgba(0,0,0,0.2); overflow: hidden;
`

const Header = styled.div` padding: 20px 22px 12px; display: flex; flex-direction: column; gap: 6px; `

const Title = styled.h3`
  margin: 0; font-size: 16px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Sub = styled.p`
  margin: 0; font-size: 12.5px; line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
`

const Body = styled.div`
  padding: 4px 22px 14px; overflow-y: auto; flex: 1; min-height: 140px; max-height: 62vh;
  display: flex; flex-direction: column; gap: 16px;
`

const Section = styled.div` display: flex; flex-direction: column; gap: 7px; `

const SectionHead = styled.div`
  font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em;
  color: #8a8f9c; padding: 6px 2px 2px;
`

const SectionNote = styled.div`
  font-size: 12px; line-height: 1.45; color: #64748b;
  padding: 0 2px 4px;
`

const Skeleton = styled.div`
  height: 58px; border-radius: 10px; background: #f1f5f9; animation: ${pulse} 1.4s ease-in-out infinite;
`

const Empty = styled.div`
  padding: 28px 16px; text-align: center; font-size: 13px; color: #64748b; line-height: 1.5;
  display: flex; flex-direction: column; gap: 6px;
`

const Row = styled.div`
  display: flex; align-items: flex-start; gap: 11px;
  padding: 11px 12px; border-radius: 10px; cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : '#ececec')};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.secondary}0F` : '#fff')};
  &:hover { border-color: #d8d8dc; }
`

const Check = styled.span`
  width: 18px; height: 18px; border-radius: 5px; flex-shrink: 0; margin-top: 1px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; color: #fff;
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.secondary : '#cbd5e1')};
  background: ${({ $on, theme }) => ($on ? theme.colors.secondary : '#fff')};
`

const RowText = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; `

const Pair = styled.div` display: flex; align-items: center; gap: 7px; flex-wrap: wrap; `

const Obligation = styled.span`
  font-size: 13px; font-weight: 700; color: ${({ theme }) => theme.colors.haiti};
`

const FileBtn = styled.button`
  font: inherit; background: none; border: none; cursor: pointer; padding: 0;
  display: inline-flex; align-items: center; gap: 4px; min-width: 0; max-width: 100%;
  color: ${({ theme }) => theme.colors.secondary};
  &:hover { text-decoration: underline; }
`

const FileName = styled.span`
  font-size: 12.5px; font-weight: 600; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const Meta = styled.div` display: flex; align-items: center; gap: 6px; flex-wrap: wrap; `

const Badge = styled.span`
  display: inline-block; padding: 1px 7px; border-radius: 9px; font-size: 10px; font-weight: 700;
  color: ${({ $text }) => $text}; background: ${({ $bg }) => $bg};
`

const TypeChip = styled.span`
  display: inline-block; padding: 1px 7px; border-radius: 9px; font-size: 10px; font-weight: 700;
  color: #475569; background: #f1f5f9;
`

const Reason = styled.span` font-size: 11px; color: #64748b; line-height: 1.35; `

const InsightBtn = styled.button`
  font: inherit; flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 4px;
  background: #fff; border: 1px solid #e4e4e7; border-radius: 999px;
  padding: 5px 11px; font-size: 11.5px; font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary}; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; background: ${({ theme }) => `${theme.colors.secondary}0d`}; }
`

const UnitRow = styled.div`
  display: flex; align-items: flex-start; gap: 11px;
  padding: 11px 12px; border-radius: 10px;
  border: 1px solid #ececec; background: #fbfcfe;
`

const UnitIcon = styled.span`
  width: 26px; height: 26px; flex-shrink: 0; border-radius: 7px;
  display: inline-flex; align-items: center; justify-content: center;
  color: #0e7490; background: #e0f2fe;
`

const ReviewBtn = styled.button`
  font: inherit; flex-shrink: 0;
  background: #fff; border: 1px solid #e4e4e7; border-radius: 999px;
  padding: 6px 13px; font-size: 12px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.secondary}; }
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 22px; border-top: 1px solid #f4f4f6;
`

const Cancel = styled.button`
  font: inherit; background: #fff; border: 1px solid #ececec; border-radius: 8px;
  padding: 8px 16px; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Save = styled.button`
  font: inherit; background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`
